import type {
  AssumeServiceDutyCommand,
  CancelKioskProductReservationCommand,
  KioskProductReservationTransitionCommand,
  ReleaseServiceDutyCommand,
  UpdateServiceRoutingConfigurationCommand,
} from "@billiard/contracts";
import {
  isAssumeServiceDutyCommand,
  isReleaseServiceDutyCommand,
  isUpdateServiceRoutingConfigurationCommand,
} from "@billiard/contracts";

export type ServiceDutyMutationCommand =
  | { kind: "UPDATE_ROUTING"; request: UpdateServiceRoutingConfigurationCommand }
  | { kind: "ASSUME"; request: AssumeServiceDutyCommand }
  | { kind: "RELEASE"; request: ReleaseServiceDutyCommand }
  | { kind: "ACKNOWLEDGE_KIOSK_PRODUCT_ORDER"; request: KioskProductReservationTransitionCommand }
  | { kind: "DELIVER_KIOSK_PRODUCT_ORDER"; request: KioskProductReservationTransitionCommand }
  | { kind: "CANCEL_KIOSK_PRODUCT_ORDER"; request: CancelKioskProductReservationCommand };

export type ServiceDutyMutationRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface ServiceDutyMutationRecovery {
  ownerIdentityKey: string;
  command: ServiceDutyMutationCommand;
  state: ServiceDutyMutationRecoveryState;
}

export type ServiceDutyMutationRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: ServiceDutyMutationRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

export const SERVICE_DUTY_MUTATION_RECOVERY_STORAGE_KEY =
  "billiard-erp:service-duty-mutation-recovery:v1";

const definiteAfterIdempotencyLookup = new Set([
  "IDEMPOTENCY_COLLISION",
  "SERVICE_DUTY_CONFIG_VERSION_CONFLICT",
  "SERVICE_DUTY_VERSION_CONFLICT",
  "SERVICE_DUTY_IDEMPOTENCY_CONFLICT",
  "SERVICE_DUTY_ALREADY_ACTIVE",
  "SERVICE_DUTY_NOT_ACTIVE",
  "SERVICE_DUTY_DISABLED",
  "SERVICE_DUTY_ASSIGNEE_MISMATCH",
  "SERVICE_DUTY_HAS_INCOMPLETE_WORK",
]);

const definiteForClaimedAttempt = new Set([
  ...definiteAfterIdempotencyLookup,
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CSRF_REJECTED",
  "FORBIDDEN",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_UPDATE_REQUIRED",
  "ORIGIN_REJECTED",
  "REQUEST_VALIDATION_FAILED",
]);

let pendingRecovery: ServiceDutyMutationRecovery | null = null;
let dispatchingRecovery: ServiceDutyMutationRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isIdentityKey(value: unknown): value is string {
  if (typeof value !== "string" || value.length < 1 || value.length > 1_024) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => typeof part === "string" && part.length >= 1 && part.length <= 256)
    );
  } catch {
    return false;
  }
}

function isKioskProductReservationTransitionCommand(
  value: unknown,
  cancel: boolean,
): value is KioskProductReservationTransitionCommand | CancelKioskProductReservationCommand {
  if (!isRecord(value)) return false;
  const keys = cancel
    ? ["reservationId", "expectedReservationVersion", "idempotencyKey", "reason"]
    : ["reservationId", "expectedReservationVersion", "idempotencyKey"];
  if (!hasExactKeys(value, keys)) return false;
  if (
    typeof value.reservationId !== "string" ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value.reservationId) ||
    typeof value.expectedReservationVersion !== "number" ||
    !Number.isSafeInteger(value.expectedReservationVersion) ||
    value.expectedReservationVersion < 1 ||
    typeof value.idempotencyKey !== "string" ||
    value.idempotencyKey.length < 8 ||
    value.idempotencyKey.length > 128
  ) {
    return false;
  }
  return (
    !cancel ||
    (typeof value.reason === "string" &&
      [...value.reason.trim()].length >= 1 &&
      [...value.reason.trim()].length <= 300)
  );
}

function isCommand(value: unknown): value is ServiceDutyMutationCommand {
  if (!isRecord(value) || !hasExactKeys(value, ["kind", "request"])) return false;
  if (value.kind === "UPDATE_ROUTING") {
    return isUpdateServiceRoutingConfigurationCommand(value.request);
  }
  if (value.kind === "ASSUME") return isAssumeServiceDutyCommand(value.request);
  if (value.kind === "RELEASE") return isReleaseServiceDutyCommand(value.request);
  if (
    value.kind === "ACKNOWLEDGE_KIOSK_PRODUCT_ORDER" ||
    value.kind === "DELIVER_KIOSK_PRODUCT_ORDER"
  ) {
    return isKioskProductReservationTransitionCommand(value.request, false);
  }
  if (value.kind === "CANCEL_KIOSK_PRODUCT_ORDER") {
    return isKioskProductReservationTransitionCommand(value.request, true);
  }
  return false;
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: ServiceDutyMutationCommand,
  state: ServiceDutyMutationRecoveryState,
): ServiceDutyMutationRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function storedRecovery(value: unknown): ServiceDutyMutationRecovery | null {
  if (!isRecord(value) || !hasExactKeys(value, ["version", "recovery"]) || value.version !== 1) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !isRecord(recovery) ||
    !hasExactKeys(recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(recovery.ownerIdentityKey) ||
    !isCommand(recovery.command) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(recovery.ownerIdentityKey, recovery.command, recovery.state);
}

function recoveryEquals(
  left: ServiceDutyMutationRecovery,
  right: ServiceDutyMutationRecovery,
): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function storage(): Storage | null | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function readStored(target: Storage): ServiceDutyMutationRecovery | null {
  const encoded = target.getItem(SERVICE_DUTY_MUTATION_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStored(recovery: ServiceDutyMutationRecovery): boolean {
  const target = storage();
  if (target === undefined) return true;
  if (target === null) return false;
  try {
    target.setItem(
      SERVICE_DUTY_MUTATION_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const saved = readStored(target);
    return saved !== null && recoveryEquals(saved, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  const target = storage();
  if (target === undefined) return;
  if (target === null) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = target.getItem(SERVICE_DUTY_MUTATION_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const saved = readStored(target);
    if (!saved) {
      recoveryUnavailable = true;
      return;
    }
    const recovery =
      saved.state === "CLAIMED"
        ? immutableRecovery(saved.ownerIdentityKey, saved.command, "OUTCOME_UNKNOWN")
        : saved;
    if (saved.state === "CLAIMED" && !saveStored(recovery)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovery;
  } catch {
    recoveryUnavailable = true;
  }
}

export function serviceDutyMutationIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

export function serviceDutyMutationOutcomeIsUnknown(
  error: unknown,
  state: ServiceDutyMutationRecoveryState,
): boolean {
  if (!isRecord(error)) return true;
  if (typeof error.statusCode === "number") {
    if (error.statusCode === 408 || error.statusCode === 429 || error.statusCode >= 500)
      return true;
    if (error.statusCode >= 400) return false;
  }
  if (typeof error.code !== "string") return true;
  return !(state === "CLAIMED"
    ? definiteForClaimedAttempt.has(error.code)
    : definiteAfterIdempotencyLookup.has(error.code));
}

export function readServiceDutyMutationRecovery(
  identityKey: string,
): ServiceDutyMutationRecoveryView {
  hydrate();
  if (recoveryUnavailable) return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimServiceDutyMutationRecovery(
  identityKey: string,
  command: ServiceDutyMutationCommand,
): ServiceDutyMutationRecoveryView {
  const current = readServiceDutyMutationRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStored(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pendingRecovery = recovery;
  return { status: "OWNED", recovery };
}

function verify(recovery: ServiceDutyMutationRecovery, identityKey: string): boolean {
  hydrate();
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const target = storage();
  if (target === undefined) return true;
  if (target === null) return false;
  try {
    const saved = readStored(target);
    return saved !== null && recoveryEquals(saved, recovery);
  } catch {
    return false;
  }
}

export function beginServiceDutyMutationDispatch(
  recovery: ServiceDutyMutationRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verify(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishServiceDutyMutationDispatch(
  recovery: ServiceDutyMutationRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markServiceDutyMutationOutcomeUnknown(
  recovery: ServiceDutyMutationRecovery,
  identityKey: string,
): boolean {
  if (!verify(recovery, identityKey)) return false;
  const unknown = immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStored(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  return true;
}

export function clearServiceDutyMutationRecovery(
  recovery: ServiceDutyMutationRecovery,
  identityKey: string,
): boolean {
  if (!verify(recovery, identityKey)) return false;
  const target = storage();
  if (target !== undefined) {
    if (target === null) return false;
    try {
      target.removeItem(SERVICE_DUTY_MUTATION_RECOVERY_STORAGE_KEY);
      if (target.getItem(SERVICE_DUTY_MUTATION_RECOVERY_STORAGE_KEY) !== null) return false;
    } catch {
      return false;
    }
  }
  pendingRecovery = null;
  return true;
}

export function resetServiceDutyMutationRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  try {
    storage()?.removeItem(SERVICE_DUTY_MUTATION_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup resets the in-memory fail-closed state as well.
  }
}

export function resetServiceDutyMutationRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
}
